// components/booking/activities/AddToCartButton.tsx - Button with visual feedback for adding items to cart
// SIMPLIFIED: Single persons/pricePerPerson instead of adults/children dual pricing

'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useActivityCart } from '@/lib/activity-cart'
import { useUnifiedCart } from '@/lib/unified-cart'
import styles from './AddToCartButton.module.css'

interface AddToCartButtonProps {
  activityId: string
  activitySlug: string
  activityName: string
  activityImage: string
  date: string
  session?: string
  persons: number
  pricePerPerson: number
  disabled?: boolean
  onSuccess?: () => void
  onError?: (error: string) => void
}

type ButtonState = 'idle' | 'loading' | 'success' | 'error'

export default function AddToCartButton({
  activityId,
  activitySlug,
  activityName,
  activityImage,
  date,
  session,
  persons,
  pricePerPerson,
  disabled = false,
  onSuccess,
  onError
}: AddToCartButtonProps) {
  const t = useTranslations('bookingActivity.addToCart')
  const { addItem } = useActivityCart()
  const { addActivityItem } = useUnifiedCart()
  const [state, setState] = useState<ButtonState>('idle')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const subtotal = persons * pricePerPerson

  const handleAddToCart = () => {
    if (persons === 0) {
      setErrorMessage(t('errorNoParticipants'))
      setState('error')
      setTimeout(() => setState('idle'), 3000)
      onError?.(t('errorNoParticipants'))
      return
    }

    if (!date) {
      setErrorMessage(t('errorNoDate'))
      setState('error')
      setTimeout(() => setState('idle'), 3000)
      onError?.(t('errorNoDate'))
      return
    }

    try {
      // Legacy activity cart (dual-write)
      addItem({
        activityId,
        activitySlug,
        activityName,
        activityImage,
        date,
        session,
        persons,
        pricePerPerson,
        adults: persons,
        children: 0,
        totalGuests: persons,
        pricePerAdult: pricePerPerson,
        pricePerChild: pricePerPerson,
        subtotal
      })

      // Unified cart (dual-write)
      addActivityItem({
        itemType: 'activity',
        activityId,
        activitySlug,
        activityName,
        activityImage,
        date,
        session,
        persons,
        pricePerPerson,
        adults: persons,
        children: 0,
        pricePerAdult: pricePerPerson,
        pricePerChild: pricePerPerson,
        subtotalUSD: subtotal,
      })

      setState('success')
      setTimeout(() => setState('idle'), 2000)
      onSuccess?.()

    } catch (error: unknown) {
      console.error('Failed to add to cart:', error)
      const errorMsg = error instanceof Error ? error.message : t('errorGeneric')
      setErrorMessage(errorMsg)
      setState('error')
      setTimeout(() => setState('idle'), 3000)
      onError?.(errorMsg)
    }
  }

  const isDisabled = disabled || state === 'loading' || state === 'success'

  return (
    <div className={styles.container}>
      <button
        className={`${styles.button} ${styles[state]}`}
        onClick={handleAddToCart}
        disabled={isDisabled}
      >
        {state === 'idle' && (
          <>
            <CartPlusIcon />
            <span>{t('button')}</span>
            <span className={styles.price}>${subtotal.toLocaleString()}</span>
          </>
        )}

        {state === 'loading' && (
          <>
            <LoadingIcon />
            <span>{t('adding')}</span>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckIcon />
            <span>{t('added')}</span>
          </>
        )}

        {state === 'error' && (
          <>
            <ErrorIcon />
            <span>{errorMessage}</span>
          </>
        )}
      </button>
    </div>
  )
}

// Icons
function CartPlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 1.66667L6.34167 3.33333H3.33333C3.10833 3.33333 2.91667 3.525 2.91667 3.75V16.25C2.91667 16.475 3.10833 16.6667 3.33333 16.6667H16.6667C16.8917 16.6667 17.0833 16.475 17.0833 16.25V3.75C17.0833 3.525 16.8917 3.33333 16.6667 3.33333H13.6583L12.5 1.66667H7.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="10" cy="10.8333" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 9.16667V12.5M8.33333 10.8333H11.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

function LoadingIcon() {
  return (
    <svg className={styles.spinner} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="50 50" opacity="0.3"/>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="25 75" strokeDashoffset="0">
        <animateTransform attributeName="transform" type="rotate" from="0 10 10" to="360 10 10" dur="1s" repeatCount="indefinite"/>
      </circle>
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2"/>
      <path d="M6.66667 10L8.88889 12.5L13.3333 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function ErrorIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2"/>
      <path d="M10 6V10.5M10 13.5H10.0083" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
}
